/**
 * Script for login page
 * @author Prajwalit <prajwalit@helpshift.com>
 * @created Jun 5, 2018
 */

import dom from "dom";
import validations from "gunpowder/utils/validation";
import xhr from "utils/xhr";
import sessionHelpers from "extras/sessionHelpers";
import {t} from "@lingui/core/macro";
import {loadTranslations} from "pages/internationalization/internationalizationLogic";
import {PREFERRED_LANG_LS_KEY} from "constants/pages";
import {DEFAULT_I18N_LANG, I18N_LANGUAGE_OPTIONS} from "constants/modals/i18nLanguageOptions";
import {errorMsgs, updateErrorMsg, updateLoginPageLanguage} from "pages/internationalization/login";
import {updateMessageBoxContent} from "pages/internationalization/messageBox";
import {renderLangOptions} from "pages/internationalization/languageDropdown";

const {i18n_enabled: i18nIsEnabled} = HSM.features;
const i18nLangOptionValues = I18N_LANGUAGE_OPTIONS.map((lang) => lang.value);

const getValue = (node) => {
  return node.getDOM().value.trim();
};

const setValue = (node, val) => {
  node.getDOM().value = val;
};

const updateError = (errNode, state, errorMsg = "") => {
  errNode.getDOM().innerText = errorMsg;

  if (state) {
    errNode.show();
  } else {
    errNode.hide();
  }
};

const usernameNode = dom.one("#username");
const passwordNode = dom.one("#password");
const emailNode = dom.one("#email-address");

const loginForm = dom.one("#login-form");
const forgotPasswordForm = dom.one("#forgot-password-form");
const googleOauthLang = dom.one("#google-login-language");

/**
 * Sets the language for the Google authentication page
 * @param {string} lang - The language selected by the user
 */
const updateGoogleOauthLang = (lang) => {
  if (googleOauthLang) {
    googleOauthLang.getDOM().value = lang;
  }
};

// Set the logged out flag in local storage to false
sessionHelpers.setUserLoggedOutStatusInLs(true);
sessionHelpers.clearPIIFromLs();

(async () => {
  const messageBoxEl = dom.one("#message-box");
  const langContainer = dom.one("#language-selector");
  const loginPrefLang = localStorage.getItem(PREFERRED_LANG_LS_KEY);
  let loginPageLang = DEFAULT_I18N_LANG;

  if (i18nIsEnabled) {
    const params = new URLSearchParams(window.location.search);
    const inviteLangParam = params.get("user-lang");
    const prefLang = inviteLangParam ?? loginPrefLang;

    if (prefLang && prefLang.toLowerCase() !== "null" && i18nLangOptionValues.includes(prefLang)) {
      loginPageLang = prefLang;
    }
  } else {
    dom.one("#language-options-container").getDOM().style.display = "none";
  }

  updateGoogleOauthLang(loginPageLang);
  renderLangOptions(loginPageLang);
  await loadTranslations(loginPageLang);

  if (messageBoxEl) {
    updateMessageBoxContent();

    langContainer.on("click", async (e) => {
      if (e.target && e.target.getDOM().dataset.lang) {
        const selectedLang = e.target.getDOM().dataset.lang;

        renderLangOptions(selectedLang);
        await loadTranslations(selectedLang);
        updateMessageBoxContent();

        localStorage.setItem(PREFERRED_LANG_LS_KEY, selectedLang);
      }
    });
  } else {
    updateLoginPageLanguage();
    updateErrorMsg();

    // Field handlers
    dom.one("#forgot-password-link").on("click", (ev) => {
      ev.preventDefault();

      loginForm.hide();
      forgotPasswordForm.show();
      emailNode.getDOM().focus();
    });

    dom.one("#login-link").on("click", (ev) => {
      ev.preventDefault();

      loginForm.show();
      forgotPasswordForm.hide();
      usernameNode.getDOM().focus();
    });

    usernameNode.on("change", () => {
      setValue(emailNode, getValue(usernameNode));
    });

    emailNode.on("change", () => {
      setValue(usernameNode, getValue(emailNode));
    });

    langContainer.on("click", async (e) => {
      if (e.target && e.target.getDOM().dataset.lang) {
        const selectedLang = e.target.getDOM().dataset.lang;

        renderLangOptions(selectedLang);

        await loadTranslations(selectedLang);
        updateLoginPageLanguage();
        updateErrorMsg();
        updateGoogleOauthLang(selectedLang);
        localStorage.setItem(PREFERRED_LANG_LS_KEY, selectedLang);
      }
    });

    // Captcha
    if (dom.one(".js-captcha")) {
      let refreshCaptchaXhr = null;

      // Fires the XHR to get new captcha image
      dom.one("#refresh-captcha-btn").on("click", () => {
        if (refreshCaptchaXhr) {
          return;
        }

        refreshCaptchaXhr = xhr({
          route: "/xhr/login/captcha/",
          onSuccess: (response) => {
            const {image, token} = response.data.captcha;
            updateCaptchaFields(image, token);
          },
          onEnd: () => {
            refreshCaptchaXhr = null;
          }
        });
      });

      // Updates captcha token & img with given data
      const updateCaptchaFields = (image, token, input = "", errorMsg = "") => {
        const cErr = dom.one("#captcha-error");

        dom.one("#captcha-img").getDOM().setAttribute("src", image);
        dom.one("#field-captcha-token").getDOM().value = token;
        dom.one("#field-captcha-input").getDOM().value = input;

        updateError(cErr, !!errorMsg, errorMsg);
      };
    }

    // Form Validation

    loginForm.on("submit", (ev) => {
      let valid = true;
      const uErr =
        dom.one("#username-error") || dom.one("#username-required") || dom.one("#username-invalid");
      const pErr = dom.one("#password-error") || dom.one("#password-required");

      updateError(uErr, false);
      updateError(pErr, false);

      if (!validations.required(getValue(usernameNode))) {
        uErr.getDOM().id = "username-required";
        updateError(uErr, true, t(errorMsgs.requiredEmail));
        valid = false;
      } else if (!validations.email(getValue(usernameNode))) {
        uErr.getDOM().id = "username-invalid";
        updateError(uErr, true, t(errorMsgs.validEmail));
        valid = false;
      }

      if (!validations.required(getValue(passwordNode))) {
        updateError(pErr, true, t(errorMsgs.requiredPassword));
        pErr.getDOM().id = "password-required";
        valid = false;
      }

      if (dom.one(".js-captcha")) {
        const captchaNode = dom.one("#field-captcha-input");
        const cErr =
          dom.one("#captcha-error") ||
          dom.one("#captcha-required") ||
          dom.one("#captcha-expired") ||
          dom.one("#captcha-invalid");

        updateError(cErr, false);

        if (!validations.required(getValue(captchaNode))) {
          cErr.getDOM().id = "captcha-required";
          updateError(cErr, true, t(errorMsgs.requiredCaptcha));
          valid = false;
        }
      }

      if (!valid) {
        ev.preventDefault();
      } else {
        pErr.getDOM().id = "password-error";
        uErr.getDOM().id = "username-error";

        if (dom.one(".js-captcha")) {
          const cErr =
            dom.one("#captcha-required") ||
            dom.one("#captcha-expired") ||
            dom.one("#captcha-invalid");

          if (cErr) {
            cErr.getDOM().id = "captcha-error";
          }
        }
      }
    });

    forgotPasswordForm.on("submit", (ev) => {
      let valid = true;
      const eErr = dom.one("#forgot-email-error") || dom.one("#forgot-email-invalid");

      updateError(eErr, false);

      if (!validations.required(getValue(emailNode))) {
        eErr.getDOM().id = "forgot-email-error";
        updateError(eErr, true, t(errorMsgs.requiredEmail));
        valid = false;
      } else if (!validations.email(getValue(emailNode))) {
        eErr.getDOM().id = "forgot-email-invalid";
        updateError(eErr, true, t(errorMsgs.validEmail));
        valid = false;
      }

      if (!valid) {
        ev.preventDefault();
      }
    });
  }
})();
